import type { TJohnDeereImportStatus } from "@services/devicesService";

/** Where a customer grants Green Growth access to an Operations Center organization. */
export const JD_CONNECTIONS_URL = "https://connections.deere.com";

/** devices.* locale keys of the John Deere import status line on a device row. */
export type JdStatusKey =
  | "jdLastImport"
  | "jdNoImportYet"
  | "jdErrorFieldOperationsDenied"
  | "jdErrorExportRefused"
  | "jdErrorThrottled"
  | "jdErrorNotConnected"
  | "jdErrorUnsupportedExport"
  | "jdErrorImportFailed"
  | "jdOpenConnections"
  | "jdOpenSettings";

export type JdProblem = {
  key: JdStatusKey;
  fallback: string;
  /**
   * Where the customer fixes it: Deere's connections page, our Settings
   * (connect John Deere), or nowhere because the import retries by itself.
   */
  fix: "deereConnections" | "settings" | null;
};

const IMPORT_FAILED: JdProblem = {
  key: "jdErrorImportFailed",
  fallback: "The last John Deere import failed. It is retried automatically.",
  fix: null,
};

/** One entry per backend JD_ERROR_CODES value (johnDeereDeviceService). */
const PROBLEMS: Record<string, JdProblem> = {
  FIELD_OPERATIONS_DENIED: {
    key: "jdErrorFieldOperationsDenied",
    fallback:
      "John Deere is not sharing this combine's harvest data. At connections.deere.com, allow Locations > Analyze Production Data for this organization.",
    fix: "deereConnections",
  },
  EXPORT_REFUSED: {
    key: "jdErrorExportRefused",
    fallback: "John Deere refused to export some of this combine's harvest data. It is retried automatically.",
    fix: null,
  },
  DEERE_THROTTLED: {
    key: "jdErrorThrottled",
    fallback: "John Deere is limiting requests right now. The import resumes automatically.",
    fix: null,
  },
  NOT_CONNECTED: {
    key: "jdErrorNotConnected",
    fallback: "John Deere is not connected for this account, so new harvest data is not imported. Connect John Deere in Settings.",
    fix: "settings",
  },
  UNSUPPORTED_EXPORT: {
    key: "jdErrorUnsupportedExport",
    fallback: "Some of this combine's harvest data from John Deere is in a format we cannot read yet. Please contact GreenGrowth support.",
    fix: null,
  },
  IMPORT_FAILED,
};

/**
 * Why new harvest data of a John Deere combine stopped arriving, or null when
 * the last import went through. An error with no code (recorded before codes
 * existed) or an unknown code reads as a failed import.
 */
export const johnDeereProblem = (status: TJohnDeereImportStatus | null | undefined): JdProblem | null => {
  if (!status) return null;
  if (status.errorCode) return PROBLEMS[status.errorCode] ?? IMPORT_FAILED;
  return status.error ? IMPORT_FAILED : null;
};
